import { useState } from 'react';
import type { ProjectView } from '@/types/current-project';
import { ProjectsSearchBar } from '@/components/shared/ProjectsSearchBar';
import { projectPayments, paymentTotal } from '@/lib/current-payments';
import { useProposalPresentation } from '@/hooks/useProposalReviews';
import { MilestonePaymentItem } from './PaymentComponents';
import { useDaoConfig } from '@/hooks/useDaoConfig';

export function PaymentsComponent({
  projects,
  account,
}: {
  projects: ProjectView[];
  account: string;
}) {
  const { currency } = useDaoConfig();
  const [selected, setSelected] = useState('');
  const [search, setSearch] = useState('');
  const project = projects.find((p) => p.projectId === selected) ?? projects[0];
  const proposal = project?.proposals.find((p) => p.status === 'Approved');
  const presentation = useProposalPresentation(
    project?.projectId ?? '',
    proposal?.proposalId
  );
  const payments = project
    ? projectPayments(project, account).filter((payment) => {
        const milestone = proposal?.milestones.find(
          (m) => m.milestoneId === payment.key
        );
        const description =
          presentation.data?.milestones.find(
            (m) => m.key === milestone?.definition.key
          )?.description ?? '';
        return (payment.title + ' ' + description)
          .toLowerCase()
          .includes(search.toLowerCase());
      })
    : [];
  return (
    <div className="space-y-10">
      <ProjectsSearchBar
        projects={projects}
        selectedProjectId={project?.projectId ?? ''}
        searchQuery={search}
        onProjectChange={setSelected}
        onSearchChange={setSearch}
      />
      {presentation.isError && (
        <p role="alert">
          Milestone descriptions could not be loaded.{' '}
          <button
            type="button"
            className="underline"
            onClick={() => void presentation.refetch()}
          >
            Retry descriptions
          </button>
        </p>
      )}
      <div className="grid grid-cols-1 items-start gap-[--spacing-16] py-[--spacing-12] font-medium lg:grid-cols-2">
        {[false, true].map((paid) => {
          const items = payments.filter((p) => p.paid === paid);
          return (
            <section
              key={String(paid)}
              className="flex min-w-0 flex-col gap-[--spacing-10]"
            >
              <div className="flex items-center justify-between text-lg font-medium text-[--text-dark-primary]">
                <h3 className="text-lg font-medium">
                  {paid ? 'Paid' : 'Awaiting Payment'}
                </h3>
                <span>{paymentTotal(items)} {currency.symbol}</span>
              </div>
              {!items.length && (
                <p
                  className={
                    'rounded-[--radi-6] border border-[--base-border] p-[--spacing-8] text-sm italic text-[--text-dark-secondary]' +
                    (paid ? '' : ' bg-[--base-surface-2]')
                  }
                >
                  {paid
                    ? 'No milestones paid yet.'
                    : 'No milestones awaiting payment.'}
                </p>
              )}
              {project &&
                items.map((payment) => (
                  <MilestonePaymentItem
                    key={payment.key}
                    project={project}
                    payment={payment}
                  />
                ))}
            </section>
          );
        })}
      </div>
    </div>
  );
}
